export interface ProTip {
  id: string;
  category: "swing" | "practice" | "strategy" | "mental" | "equipment" | "putting" | "short-game";
  title: string;
  description: string;
  isUnlocked: boolean;
  requiredShares?: number;
}

// Base pro tips - always available
export const baseTips: ProTip[] = [
  {
    id: "base-1",
    category: "swing",
    title: "Focus on Tempo",
    description: "A smooth swing beats a fast one every time.",
    isUnlocked: true,
  },
  {
    id: "base-2", 
    category: "strategy",
    title: "Aim for Center",
    description: "Aim for the center of greens, not the pins – fewer risks, more pars.",
    isUnlocked: true,
  },
  {
    id: "base-3",
    category: "mental", 
    title: "Reset Quickly",
    description: "Don't dwell on bad shots - reset and focus on the next one.",
    isUnlocked: true,
  },
  {
    id: "base-4",
    category: "swing",
    title: "Keep Your Head Still",
    description: "A stable head position leads to consistent ball striking.",
    isUnlocked: true,
  },
  {
    id: "base-5",
    category: "putting",
    title: "Read the Green",
    description: "Always read putts from behind the ball to see the true break.",
    isUnlocked: true,
  },
  {
    id: "base-6",
    category: "strategy",
    title: "Play to Your Strengths",
    description: "Know your game and play shots you're confident with.",
    isUnlocked: true,
  }
];

// Premium tips - unlocked by sharing
export const premiumTips: ProTip[] = [
  // Level 1 (1 share each)
  {
    id: "premium-1",
    category: "swing",
    title: "Balance Equals Control",
    description: "Always finish your swing balanced; balance equals control.",
    isUnlocked: false,
    requiredShares: 1,
  },
  {
    id: "premium-2",
    category: "practice",
    title: "Mix Your Practice",
    description: "Spend equal time on driving, irons, chipping, and putting.",
    isUnlocked: false,
    requiredShares: 1,
  },
  {
    id: "premium-3",
    category: "swing",
    title: "Grip Pressure",
    description: "Hold the club like you're holding a bird - firm enough to control, gentle enough not to hurt it.",
    isUnlocked: false,
    requiredShares: 1,
  },
  {
    id: "premium-4",
    category: "putting",
    title: "Follow Through",
    description: "Make your putting stroke follow through to the hole - short strokes lead to short putts.",
    isUnlocked: false,
    requiredShares: 1,
  },
  {
    id: "premium-5",
    category: "strategy",
    title: "Think Ahead",
    description: "Think one shot ahead – set yourself up for the easiest next shot.",
    isUnlocked: false,
    requiredShares: 1,
  },

  // Level 2 (2 shares each)
  {
    id: "premium-6",
    category: "practice", 
    title: "Use Alignment Tools",
    description: "Use alignment sticks or markers to ensure your stance and aim are consistent.",
    isUnlocked: false,
    requiredShares: 2,
  },
  {
    id: "premium-7",
    category: "swing",
    title: "Weight Transfer",
    description: "Shift your weight from back foot to front foot during the swing for power and accuracy.",
    isUnlocked: false,
    requiredShares: 2,
  },
  {
    id: "premium-8",
    category: "short-game",
    title: "Chipping Technique",
    description: "Use a putting stroke for chipping - keep your hands ahead of the clubhead.",
    isUnlocked: false,
    requiredShares: 2,
  },
  {
    id: "premium-9",
    category: "mental",
    title: "Visualize Success",
    description: "Picture the perfect shot before you swing - see it, feel it, trust it.",
    isUnlocked: false,
    requiredShares: 2,
  },
  {
    id: "premium-10",
    category: "strategy",
    title: "Course Management",
    description: "Play the percentages - take the safe route when trouble is in play.",
    isUnlocked: false,
    requiredShares: 2,
  },

  // Level 3 (3 shares each)
  {
    id: "premium-11",
    category: "mental",
    title: "Pre-Shot Routine",
    description: "Build a pre-shot routine and stick to it under pressure.",
    isUnlocked: false,
    requiredShares: 3,
  },
  {
    id: "premium-12",
    category: "equipment",
    title: "Annual Regrip",
    description: "Regrip your clubs annually – worn grips cause mishits.",
    isUnlocked: false,
    requiredShares: 3,
  },
  {
    id: "premium-13",
    category: "swing",
    title: "Swing Plane",
    description: "Keep your swing on plane - imagine swinging inside a barrel.",
    isUnlocked: false,
    requiredShares: 3,
  },
  {
    id: "premium-14",
    category: "putting",
    title: "Distance Control",
    description: "Focus on distance first, then line - you can't make a putt that doesn't reach the hole.",
    isUnlocked: false,
    requiredShares: 3,
  },
  {
    id: "premium-15",
    category: "practice",
    title: "Practice with Purpose",
    description: "Every practice shot should have a specific goal - don't just hit balls mindlessly.",
    isUnlocked: false,
    requiredShares: 3,
  },

  // Level 4 (4 shares each)
  {
    id: "premium-16",
    category: "equipment",
    title: "Ball Position Matters",
    description: "Check ball position – small adjustments can fix slices or hooks.",
    isUnlocked: false,
    requiredShares: 4,
  },
  {
    id: "premium-17",
    category: "swing",
    title: "Release the Club",
    description: "Let the club release naturally through impact - don't force it.",
    isUnlocked: false,
    requiredShares: 4,
  },
  {
    id: "premium-18",
    category: "short-game",
    title: "Bunker Play",
    description: "Open your stance and clubface in bunkers - swing along your body line.",
    isUnlocked: false,
    requiredShares: 4,
  },
  {
    id: "premium-19",
    category: "strategy",
    title: "Wind Strategy",
    description: "Play more club into the wind, less club with the wind - adjust for crosswinds.",
    isUnlocked: false,
    requiredShares: 4,
  },
  {
    id: "premium-20",
    category: "mental",
    title: "Stay Present",
    description: "Focus only on the shot at hand - past and future don't matter right now.",
    isUnlocked: false,
    requiredShares: 4,
  },

  // Level 5 (5 shares each)
  {
    id: "premium-21",
    category: "swing",
    title: "Lag in the Downswing",
    description: "Maintain wrist angle in the downswing for maximum power and control.",
    isUnlocked: false,
    requiredShares: 5,
  },
  {
    id: "premium-22",
    category: "putting",
    title: "Read Grain",
    description: "Learn to read grain direction - putts break more with the grain than against it.",
    isUnlocked: false,
    requiredShares: 5,
  },
  {
    id: "premium-23",
    category: "short-game",
    title: "Lob Shot Technique",
    description: "Open the clubface, swing steep, and accelerate through for high, soft shots.",
    isUnlocked: false,
    requiredShares: 5,
  },
  {
    id: "premium-24",
    category: "strategy",
    title: "Pin Hunting",
    description: "Only attack pins when you have a good lie and clear shot - otherwise play safe.",
    isUnlocked: false,
    requiredShares: 5,
  },
  {
    id: "premium-25",
    category: "practice",
    title: "Pressure Practice",
    description: "Practice with consequences - make every shot count to simulate pressure.",
    isUnlocked: false,
    requiredShares: 5,
  },

  // Level 6 (6 shares each)
  {
    id: "premium-26",
    category: "swing",
    title: "Sequencing",
    description: "Start the downswing with your lower body, then arms, then hands.",
    isUnlocked: false,
    requiredShares: 6,
  },
  {
    id: "premium-27",
    category: "equipment",
    title: "Shaft Fitting",
    description: "Get fitted for the right shaft flex - it affects distance and accuracy more than you think.",
    isUnlocked: false,
    requiredShares: 6,
  },
  {
    id: "premium-28",
    category: "putting",
    title: "Green Reading",
    description: "Read putts from multiple angles - low side, high side, and behind the hole.",
    isUnlocked: false,
    requiredShares: 6,
  },
  {
    id: "premium-29",
    category: "mental",
    title: "Confidence Building",
    description: "Build confidence through preparation - know your yardages and trust your swing.",
    isUnlocked: false,
    requiredShares: 6,
  },
  {
    id: "premium-30",
    category: "strategy",
    title: "Risk vs Reward",
    description: "Calculate risk vs reward on every shot - sometimes par is a great score.",
    isUnlocked: false,
    requiredShares: 6,
  },

  // Level 7 (7 shares each)
  {
    id: "premium-31",
    category: "swing",
    title: "Impact Position",
    description: "Hands ahead of clubhead at impact - this creates solid contact and control.",
    isUnlocked: false,
    requiredShares: 7,
  },
  {
    id: "premium-32",
    category: "short-game",
    title: "Flop Shot",
    description: "Open stance, open clubface, steep swing - perfect for short-sided shots.",
    isUnlocked: false,
    requiredShares: 7,
  },
  {
    id: "premium-33",
    category: "putting",
    title: "Speed Control",
    description: "Practice putting to different distances - speed is more important than line.",
    isUnlocked: false,
    requiredShares: 7,
  },
  {
    id: "premium-34",
    category: "mental",
    title: "Pre-Round Prep",
    description: "Arrive early, warm up properly, and visualize your round before you start.",
    isUnlocked: false,
    requiredShares: 7,
  },
  {
    id: "premium-35",
    category: "strategy",
    title: "Course Knowledge",
    description: "Study the course before you play - know where the trouble is and avoid it.",
    isUnlocked: false,
    requiredShares: 7,
  },

  // Level 8 (8 shares each)
  {
    id: "premium-36",
    category: "swing",
    title: "Swing Arc",
    description: "Widen your swing arc for more power - think big, smooth, and connected.",
    isUnlocked: false,
    requiredShares: 8,
  },
  {
    id: "premium-37",
    category: "equipment",
    title: "Ball Selection",
    description: "Choose the right ball for your game - soft for feel, hard for distance.",
    isUnlocked: false,
    requiredShares: 8,
  },
  {
    id: "premium-38",
    category: "short-game",
    title: "Bump and Run",
    description: "Use a 7-iron for bump and run shots around the green - low and running.",
    isUnlocked: false,
    requiredShares: 8,
  },
  {
    id: "premium-39",
    category: "mental",
    title: "Routine Under Pressure",
    description: "Stick to your routine even when nervous - it's your anchor in tough situations.",
    isUnlocked: false,
    requiredShares: 8,
  },
  {
    id: "premium-40",
    category: "strategy",
    title: "Layup Strategy",
    description: "Sometimes laying up is the smart play - position yourself for the next shot.",
    isUnlocked: false,
    requiredShares: 8,
  },

  // Level 9 (9 shares each)
  {
    id: "premium-41",
    category: "swing",
    title: "Connection",
    description: "Keep your arms connected to your body in the backswing for consistency.",
    isUnlocked: false,
    requiredShares: 9,
  },
  {
    id: "premium-42",
    category: "putting",
    title: "Stroke Path",
    description: "Keep your putting stroke on a straight line - avoid arcing the putter.",
    isUnlocked: false,
    requiredShares: 9,
  },
  {
    id: "premium-43",
    category: "short-game",
    title: "Spin Control",
    description: "Learn to control spin on short shots - sometimes you want it to check, sometimes run.",
    isUnlocked: false,
    requiredShares: 9,
  },
  {
    id: "premium-44",
    category: "mental",
    title: "Focus Techniques",
    description: "Use breathing and visualization to stay focused during your round.",
    isUnlocked: false,
    requiredShares: 9,
  },
  {
    id: "premium-45",
    category: "strategy",
    title: "Weather Adjustments",
    description: "Adjust your strategy for wind, rain, and temperature - conditions change everything.",
    isUnlocked: false,
    requiredShares: 9,
  },

  // Level 10 (10 shares each)
  {
    id: "premium-46",
    category: "swing",
    title: "Timing",
    description: "Perfect timing comes from rhythm and tempo - practice with a metronome.",
    isUnlocked: false,
    requiredShares: 10,
  },
  {
    id: "premium-47",
    category: "equipment",
    title: "Lie Angle",
    description: "Get your clubs fitted for proper lie angle - it affects direction and consistency.",
    isUnlocked: false,
    requiredShares: 10,
  },
  {
    id: "premium-48",
    category: "putting",
    title: "Green Speed",
    description: "Adjust your stroke for green speed - faster greens need shorter strokes.",
    isUnlocked: false,
    requiredShares: 10,
  },
  {
    id: "premium-49",
    category: "mental",
    title: "Pressure Management",
    description: "Embrace pressure - it means you're in a position to succeed.",
    isUnlocked: false,
    requiredShares: 10,
  },
  {
    id: "premium-50",
    category: "strategy",
    title: "Scoring Zones",
    description: "Identify your scoring zones on each hole - where you can attack vs. where to play safe.",
    isUnlocked: false,
    requiredShares: 10,
  }
];

// Get user's share count from localStorage
export const getShareCount = (): number => {
  try {
    return parseInt(localStorage.getItem("clubiq-share-count") || "0");
  } catch {
    return 0;
  }
};

// Increment share count
export const incrementShareCount = (): number => {
  const currentCount = getShareCount();
  const newCount = currentCount + 1;
  localStorage.setItem("clubiq-share-count", newCount.toString());
  return newCount;
};

// Get available tips based on share count
export const getAvailableTips = (): ProTip[] => {
  const shareCount = getShareCount();
  
  const unlockedPremiumTips = premiumTips.map(tip => ({
    ...tip,
    isUnlocked: shareCount >= (tip.requiredShares || 0)
  }));
  
  return [...baseTips, ...unlockedPremiumTips];
};

// Get next tip to unlock
export const getNextTipToUnlock = (): ProTip | null => {
  const shareCount = getShareCount();
  
  const nextTip = premiumTips.find(tip => 
    !tip.isUnlocked && (tip.requiredShares || 0) === shareCount + 1
  );
  
  return nextTip || null;
};

// Share URL generation with unique tracking
export const generateShareUrl = (): string => {
  const baseUrl = window.location.origin;
  const userId = Date.now().toString(36); // Simple user tracking
  const shareCode = Math.random().toString(36).substring(2, 8);
  return `${baseUrl}?ref=${userId}-${shareCode}`;
};

// Share text generation
export const generateShareText = (): string => {
  return "Check out ClubIQ - an AI-powered golf assistant that's helping me improve my game! Get personalized recommendations, distance analysis, and equipment suggestions. 🏌️⛳";
};

// Removed unused validateShare function

// Social sharing functions with validation
export const shareToTwitter = async () => {
  const url = generateShareUrl();
  const text = generateShareText();
  const shareWindow = window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  
  if (shareWindow) {
    // Wait a moment then count the share
    setTimeout(() => {
      incrementShareCount();
    }, 2000);
  }
};

export const shareToFacebook = async () => {
  const url = generateShareUrl();
  const shareWindow = window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  
  if (shareWindow) {
    setTimeout(() => {
      incrementShareCount();
    }, 2000);
  }
};

export const shareToLinkedIn = async () => {
  const url = generateShareUrl();
  const text = generateShareText();
  const shareWindow = window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
  
  if (shareWindow) {
    setTimeout(() => {
      incrementShareCount();
    }, 2000);
  }
};

export const copyToClipboard = async () => {
  const url = generateShareUrl();
  try {
    await navigator.clipboard.writeText(url);
    // For copy, we increment immediately since user has the link
    incrementShareCount();
    return true;
  } catch {
    return false;
  }
};
